/**
 * Centralized Timeout Constants
 *
 * This file defines all timeout values used throughout the application and tests.
 * The timeout values follow a hierarchical relationship to prevent timing conflicts.
 *
 * HIERARCHY RULES:
 * 1. AUTH_TIMEOUT > NETWORK_TIMEOUT (auth includes retries and processing)
 * 2. TEST_TIMEOUT > UI_TIMEOUT (tests must wait longer than users)
 * 3. EMAIL_TIMEOUT > AUTH_TIMEOUT (email delivery is slower than auth operations)
 * 4. CSS_TIMEOUT < TEST_TIMEOUT (styling should load faster than test expectations)
 */

// [SEL-5] Added EMAIL_DELIVERY_TIMEOUT, EMAIL_EXTENDED_TIMEOUT, EMAIL_REQUEST_TIMEOUT, EMAIL_WAIT_INTERVAL
// [OSPEC:smoke-testing] Timeout constants used by smoke tests for page load and element assertions

// =============================================================================
// NETWORK OPERATIONS (15s - 30s range)
// Base timeouts for HTTP requests and API calls
// =============================================================================

/**
 * Timeout for loading /config.json at app startup
 * Used in: src/config/runtime-config.ts (runtimeConfig.load)
 * If config.json doesn't load within 5s, fall back to build-time env vars
 * Must be short to prevent blocking app startup during tests or when config unavailable
 */
export const CONFIG_LOAD_TIMEOUT = 5000;

/**
 * Base timeout for HTTP fetch operations and Supabase API calls
 * Used in: src/integrations/supabase/client.ts (AbortController timeout)
 * Should be shorter than AUTH timeouts to allow for retries
 *
 * WEBKIT NOTE: WebKit can take 10-12s for Supabase auth requests (slower than Chromium).
 * Using 30s provides 2.5x safety margin over observed 12s max response time.
 */
export const NETWORK_REQUEST_TIMEOUT = 30000;

/**
 * Extended timeout for network operations that may be slower
 * Used for: Edge functions, file uploads, complex queries
 */
export const NETWORK_EXTENDED_TIMEOUT = 30000;

// =============================================================================
// AUTHENTICATION OPERATIONS (20s - 25s range)
// Must be longer than NETWORK timeouts to allow for retries
// =============================================================================

/**
 * Timeout for user signup operations including email confirmation setup
 * Used in: src/contexts/AuthContext.tsx (signUp function)
 * Must be > NETWORK_REQUEST_TIMEOUT to allow for retry attempts
 *
 * WEBKIT NOTE: Increased to 50s to accommodate Supabase's inconsistent response times.
 * Supabase /auth/v1/signup can be fast (<5s) or slow (>10s) depending on load/throttling.
 * Three 15s attempts with backoff give best chance of success without excessive wait.
 */
export const AUTH_SIGNUP_TIMEOUT = 50000;

/**
 * Retry configuration for signup operations
 * Formula: (AUTH_SIGNUP_TIMEOUT - total_cooldown) / max_attempts = per_attempt_timeout
 * Total budget: 15s + 2s + 15s + 3s + 15s = 50s
 *
 * WEBKIT NOTE: Three attempts with progressive backoff (2s/3s delays) maximizes success rate.
 * 15s per attempt accommodates Supabase's variable response times while still allowing retries.
 * Still faster than NETWORK_REQUEST_TIMEOUT (30s), so network layer acts as safety net.
 */
export const AUTH_SIGNUP_MAX_ATTEMPTS = 3;
export const AUTH_SIGNUP_ATTEMPT_TIMEOUT = (AUTH_SIGNUP_TIMEOUT - 5000) / AUTH_SIGNUP_MAX_ATTEMPTS; // 15s
export const AUTH_SIGNUP_RETRY_DELAY = 1000; // Base delay (multiplied by attempt number: 2s, 3s)
export const MAIL_RETRY_DELAY = 1000; // Base delay (multiplied by attempt number: 2s, 3s)

/**
 * Timeout for user signin operations
 * Used in: src/contexts/AuthContext.tsx (signIn function)
 *
 * WEBKIT NOTE: Set to 35s to match AUTH_SIGNUP_TIMEOUT for consistency.
 * Must be > NETWORK_REQUEST_TIMEOUT (30s) to prevent premature auth layer timeout.
 * While signin is simpler than signup, both use the same network layer and face
 * the same WebKit slowness, so they should have the same timeout.
 */
export const AUTH_SIGNIN_TIMEOUT = 35000;

/**
 * Timeout for session establishment after authentication
 * Used in: Navigation flows, auth state changes
 * Allows time for session to be established and validated
 */
export const AUTH_SESSION_TIMEOUT = 25000;

// =============================================================================
// USER INTERFACE OPERATIONS (1s - 10s range)
// Timeouts for UI feedback, loading states, and user interactions
// =============================================================================

/**
 * Short delay for UI retry operations between attempts
 * Used in: Form submissions, toast checks, button state changes
 * Should be long enough to avoid rapid-fire retries
 */
export const UI_RETRY_DELAY = 1000;

/**
 * Timeout for UI feedback elements like toasts and notifications
 * Used in: Toast visibility checks, success/error message display, modal transitions
 * Should be long enough for users to see feedback but not too long
 *
 * IMPORTANT: This timeout accounts for CI environments where animations,
 * state updates, and rendering can take longer than on local development machines.
 * 10s provides sufficient buffer for: modal animations (500ms), database operations (2-3s),
 * React re-renders (1-2s), and additional CI overhead (2-3s).
 */
export const UI_FEEDBACK_TIMEOUT = 10000;

/**
 * Timeout for form loading states and button disabled periods
 * Used in: src/pages/SignUp.tsx (checkout timeout)
 * Prevents indefinite loading states if auth flow stalls
 */
export const UI_LOADING_TIMEOUT = 10000;

/**
 * Duration for toast messages to remain visible
 * Used in: src/contexts/AuthContext.tsx, src/hooks/use-toast.ts
 * Long enough for users to read but not intrusive
 */
export const TOAST_DISPLAY_DURATION = 120000;

/**
 * Very long toast duration for critical messages that shouldn't auto-dismiss
 * Used in: Success messages, important notifications
 * Effectively disables auto-dismiss (16+ minutes)
 */
export const TOAST_PERSISTENT_DURATION = 1000000;

// =============================================================================
// CSS AND RENDERING OPERATIONS (5s - 15s range)
// Timeouts for stylesheet loading, font rendering, and visual stability
// =============================================================================

/**
 * Base timeout for CSS verification operations
 * Used in: tests/helpers/css-verification.ts (most functions)
 * Should be long enough for stylesheets to load but shorter than test timeouts
 */
export const CSS_VERIFICATION_TIMEOUT = 10000;

/**
 * Shorter timeout for CSS operations that should be fast
 * Used in: Button state change
 * For operations that should complete quickly or fallback
 */
export const CSS_BUTTON_STATE_CHANGE = 500;

/**
 * Shorter timeout for CSS operations that should be fast
 * Used in: Font loading, CSS variable checks, animation completion
 * For operations that should complete quickly or fallback
 */
export const CSS_FAST_TIMEOUT = 5000;

/**
 * Extended timeout for complex CSS operations
 * Used in: waitForCSSReady with multiple checks, visual stability
 * For comprehensive CSS verification that may take longer
 */
export const CSS_EXTENDED_TIMEOUT = 15000;

/**
 * Minimal timeout for paint operations and immediate UI updates
 * Used in: waitForTimeout calls, animation delays
 * Just long enough for a repaint cycle
 */
export const CSS_PAINT_DELAY = 200;

/**
 * Timeout for visual stability checks
 * Used in: waitForVisualStability function
 * Long enough to detect layout shifts but not too slow
 */
export const CSS_STABILITY_TIMEOUT = 3000;

/**
 * Duration before OfflineIndicator reconnected banner auto-hides
 * Used in: src/hooks/useNetworkStatus.ts (setTimeout duration)
 * Banner shows for 3 seconds after reconnection before dismissing
 */
export const OFFLINE_BANNER_RECONNECT_DURATION = 3000;

/**
 * Duration for OfflineIndicator banner auto-hide (test verification)
 * Used in: tests/error-handling/offline-indicator.e2e.test.ts
 * Banner auto-hides after 3 seconds, tests wait 3.5s to verify dismissal
 */
export const OFFLINE_BANNER_AUTO_HIDE_DURATION = 3500;

/**
 * Interval for checking visual stability between snapshots
 * Used in: waitForVisualStability polling
 * Frequent enough to catch changes but not excessive
 */
export const CSS_STABILITY_CHECK_INTERVAL = 100;

// =============================================================================
// TEST OPERATIONS (10s - 30s range)
// Must be longer than UI timeouts to wait for user interactions
// =============================================================================

/**
 * Standard timeout for test element interactions
 * Used in: Playwright test assertions, element visibility checks
 * Should be longer than UI_FEEDBACK_TIMEOUT to catch delayed elements
 *
 * IMPORTANT: Must be > UI_FEEDBACK_TIMEOUT (10s) to maintain timeout hierarchy.
 * 15s provides sufficient buffer for UI feedback to appear plus additional test overhead.
 */
export const TEST_ELEMENT_TIMEOUT = 15000;

/**
 * Extended timeout for complex test operations
 * Used in: Form submissions, page navigation, auth flows in tests
 * Allows time for multiple UI updates and network requests
 */
export const TEST_EXTENDED_TIMEOUT = 20000;

/**
 * Short timeout for test operations that should be quick
 * Used in: Element state checks, button clicks, form fills, email waiting
 * For operations that should succeed quickly or fail fast
 *
 * Increased from 5s to 15s to accommodate infrastructure delays in CI/DEV environments
 * where email delivery and Mailpit operations may take longer
 */
export const TEST_IMMEDIATE_TIMEOUT = 10000;

/**
 * Timeout for waiting for auth operations to complete AND UI feedback to appear
 * Used in: Signup/signin tests waiting for success toasts
 * Must be: AUTH_SIGNUP_TIMEOUT + UI_FEEDBACK_TIMEOUT to allow full operation cycle
 * This prevents tests from giving up before the auth operation completes
 */
export const TEST_AUTH_OPERATION_TIMEOUT = AUTH_SIGNUP_TIMEOUT + UI_FEEDBACK_TIMEOUT; // 60s

/**
 * Timeout for file download operations in tests
 * Used in: E2E tests for export functionality (DOCX, RTF, HTML, ZIP)
 * Must be longer than default Playwright timeout (30s) because:
 * - DOCX generation involves XML generation + ZIP compression
 * - CI runners have variable performance
 * - Large exports may take significant time to generate
 */
export const TEST_DOWNLOAD_TIMEOUT = 60000;

// =============================================================================
// EMAIL OPERATIONS (30s - 60s range)
// Longest timeouts due to external service dependencies
// =============================================================================

/**
 * Standard timeout for waiting for emails to arrive
 * Used in: tests/helpers/mailslurp.ts, email verification flows
 * Must be longer than AUTH_SIGNUP_TIMEOUT as emails arrive after signup
 *
 * WEBKIT NOTE: Increased to 60s to maintain hierarchy (must be > AUTH_SIGNUP_TIMEOUT=50s).
 * Provides 10s buffer for email delivery after signup completes, accounting for
 * Supabase's variable response times plus email service delivery delays.
 */
export const EMAIL_DELIVERY_TIMEOUT = 60000;

/**
 * Extended timeout for email operations that may be slower
 * Used in: Complex email verification, multiple email checks
 * For situations where email delivery may be delayed
 * Increased to 90s to accommodate slower email delivery in CI/concurrent test runs
 */
export const EMAIL_EXTENDED_TIMEOUT = 90000;
export const EMAIL_REQUEST_TIMEOUT = 30000;

/**
 * Polling interval for checking email arrival
 * Used in: waitForEmail polling loops
 * Frequent enough to be responsive but not excessive on API usage
 */
export const EMAIL_POLL_INTERVAL = 2000;

/**
 * Short delay after triggering email send before checking delivery
 * Used in: Test setup (beforeEach) after signup/password reset to allow email to reach Mailpit
 * Accounts for local Mailpit delivery latency in test environments
 *
 * Increased from 5s to 10s to accommodate infrastructure delays in CI/DEV environments
 * where Supabase → Mailpit email delivery may be slower
 */
export const EMAIL_SETUP_DELAY = 10000;

/**
 * Timeout for waitForEmail operations
 * Used in: Email waiting tests, polling with timeout
 * Longer than EMAIL_POLL_INTERVAL to allow multiple poll attempts
 *
 * Increased from 10s to 30s to accommodate infrastructure delays in CI/DEV environments
 * where Mailpit may take longer to receive and index emails from Supabase
 */
export const EMAIL_WAIT_INTERVAL = 30000;

// =============================================================================
// VALIDATION AND RELATIONSHIPS
// Runtime checks to ensure timeout hierarchy is maintained
// =============================================================================

/**
 * Timeout values for validation (allows override for testing)
 */
export interface TimeoutOverrides {
  AUTH_SIGNUP_TIMEOUT?: number;
  AUTH_SIGNIN_TIMEOUT?: number;
  NETWORK_REQUEST_TIMEOUT?: number;
  TEST_ELEMENT_TIMEOUT?: number;
  UI_FEEDBACK_TIMEOUT?: number;
  EMAIL_DELIVERY_TIMEOUT?: number;
  CSS_VERIFICATION_TIMEOUT?: number;
  OFFLINE_BANNER_AUTO_HIDE_DURATION?: number;
  OFFLINE_BANNER_RECONNECT_DURATION?: number;
}

/**
 * Validates that timeout hierarchy rules are maintained
 * Throws error if any timeout relationships are violated
 *
 * @param overrides - Optional timeout overrides for testing violation branches
 */
export function validateTimeoutHierarchy(overrides: TimeoutOverrides = {}): void {
  const violations: string[] = [];

  // Use overrides or actual constants
  const authSignup = overrides.AUTH_SIGNUP_TIMEOUT ?? AUTH_SIGNUP_TIMEOUT;
  const authSignin = overrides.AUTH_SIGNIN_TIMEOUT ?? AUTH_SIGNIN_TIMEOUT;
  const networkRequest = overrides.NETWORK_REQUEST_TIMEOUT ?? NETWORK_REQUEST_TIMEOUT;
  const testElement = overrides.TEST_ELEMENT_TIMEOUT ?? TEST_ELEMENT_TIMEOUT;
  const uiFeedback = overrides.UI_FEEDBACK_TIMEOUT ?? UI_FEEDBACK_TIMEOUT;
  const emailDelivery = overrides.EMAIL_DELIVERY_TIMEOUT ?? EMAIL_DELIVERY_TIMEOUT;
  const cssVerification = overrides.CSS_VERIFICATION_TIMEOUT ?? CSS_VERIFICATION_TIMEOUT;
  const offlineAutoHide = overrides.OFFLINE_BANNER_AUTO_HIDE_DURATION ?? OFFLINE_BANNER_AUTO_HIDE_DURATION;
  const offlineReconnect = overrides.OFFLINE_BANNER_RECONNECT_DURATION ?? OFFLINE_BANNER_RECONNECT_DURATION;

  // Rule 1: AUTH_TIMEOUT > NETWORK_TIMEOUT
  if (authSignup < networkRequest) {
    violations.push(`AUTH_SIGNUP_TIMEOUT (${authSignup}) must be >= NETWORK_REQUEST_TIMEOUT (${networkRequest})`);
  }

  // Rule 1b: AUTH_TIMEOUT > NETWORK_TIMEOUT
  if (authSignin < networkRequest) {
    violations.push(`AUTH_SIGNIN_TIMEOUT (${authSignin}) must be >= NETWORK_REQUEST_TIMEOUT (${networkRequest})`);
  }

  // Rule 2: TEST_TIMEOUT > UI_TIMEOUT
  if (testElement <= uiFeedback) {
    violations.push(`TEST_ELEMENT_TIMEOUT (${testElement}) must be > UI_FEEDBACK_TIMEOUT (${uiFeedback})`);
  }

  // Rule 3: EMAIL_TIMEOUT > AUTH_TIMEOUT
  if (emailDelivery <= authSignup) {
    violations.push(`EMAIL_DELIVERY_TIMEOUT (${emailDelivery}) must be > AUTH_SIGNUP_TIMEOUT (${authSignup})`);
  }

  // Rule 4: CSS_TIMEOUT <= TEST_TIMEOUT
  if (cssVerification > testElement) {
    violations.push(`CSS_VERIFICATION_TIMEOUT (${cssVerification}) must be <= TEST_ELEMENT_TIMEOUT (${testElement})`);
  }

  // Rule 5: OFFLINE_BANNER_AUTO_HIDE_DURATION >= OFFLINE_BANNER_RECONNECT_DURATION (test waits longer than actual duration)
  if (offlineAutoHide < offlineReconnect) {
    violations.push(`OFFLINE_BANNER_AUTO_HIDE_DURATION (${offlineAutoHide}) must be >= OFFLINE_BANNER_RECONNECT_DURATION (${offlineReconnect})`);
  }

  if (violations.length > 0) {
    throw new Error(`Timeout hierarchy violations detected:\n${violations.join('\n')}`);
  }
}

// =============================================================================
// TIMEOUT CATEGORIES FOR TYPE SAFETY
// =============================================================================

/**
 * Categories of timeout operations for better organization
 */
export enum TimeoutCategory {
  NETWORK = 'network',
  AUTH = 'auth',
  UI = 'ui',
  CSS = 'css',
  TEST = 'test',
  EMAIL = 'email'
}

/**
 * Mapping of categories to their timeout values
 * Useful for debugging, filtering, and organization by timeout type
 */
export const TIMEOUTS_BY_CATEGORY = {
  [TimeoutCategory.NETWORK]: [
    NETWORK_REQUEST_TIMEOUT,
    NETWORK_EXTENDED_TIMEOUT
  ],
  [TimeoutCategory.AUTH]: [
    AUTH_SIGNUP_TIMEOUT,
    AUTH_SIGNIN_TIMEOUT,
    AUTH_SESSION_TIMEOUT
  ],
  [TimeoutCategory.UI]: [
    UI_RETRY_DELAY,
    UI_FEEDBACK_TIMEOUT,
    UI_LOADING_TIMEOUT,
    TOAST_DISPLAY_DURATION,
    TOAST_PERSISTENT_DURATION
  ],
  [TimeoutCategory.CSS]: [
    CSS_VERIFICATION_TIMEOUT,
    CSS_FAST_TIMEOUT,
    CSS_EXTENDED_TIMEOUT,
    CSS_PAINT_DELAY,
    CSS_STABILITY_TIMEOUT,
    CSS_STABILITY_CHECK_INTERVAL
  ],
  [TimeoutCategory.TEST]: [
    TEST_ELEMENT_TIMEOUT,
    TEST_EXTENDED_TIMEOUT,
    TEST_IMMEDIATE_TIMEOUT,
    TEST_AUTH_OPERATION_TIMEOUT
  ],
  [TimeoutCategory.EMAIL]: [
    EMAIL_DELIVERY_TIMEOUT,
    EMAIL_EXTENDED_TIMEOUT,
    EMAIL_POLL_INTERVAL
  ]
} as const;

// Validate hierarchy on module load (development only)
if (process.env.NODE_ENV === 'development') {
  validateTimeoutHierarchy();
}
